import { useEffect, useMemo, useState } from "react";
import { CaretLeft, Minus, Plus, Printer, Receipt, Trash, UserSwitch, X } from "@phosphor-icons/react";
import { BrandWordmark } from "../components/Brand";
import { zar, fmtTime } from "../lib/format";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function KioskPos({ session, kioskApi, onExit }) {
  const [boot, setBoot] = useState(null);
  const [view, setView] = useState("tables"); // tables | order | receipt
  const [order, setOrder] = useState(null);
  const [section, setSection] = useState(null);   // top-level category id
  const [subcat, setSubcat] = useState(null);     // selected deeper category id
  const [closing, setClosing] = useState(false);
  const [tip, setTip] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await kioskApi("get", "/kiosk/pos/bootstrap");
      setBoot(data);
    } catch {
      toast.error("Could not load POS data");
      onExit();
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo(() => (boot?.categories || []).filter((c) => !c.parent_id).sort((a, b) => a.sort - b.sort), [boot]);
  const catById = useMemo(() => Object.fromEntries((boot?.categories || []).map((c) => [c.id, c])), [boot]);
  const childrenOf = (id) => (boot?.categories || []).filter((c) => c.parent_id === id).sort((a, b) => a.sort - b.sort);
  const subtreeIds = (id) => {
    const out = [id];
    (boot?.categories || []).forEach((c) => { if (c.parent_id && out.includes(c.parent_id)) out.push(c.id); });
    // iterate to fix ordering dependence
    let changed = true;
    while (changed) {
      changed = false;
      (boot?.categories || []).forEach((c) => { if (c.parent_id && out.includes(c.parent_id) && !out.includes(c.id)) { out.push(c.id); changed = true; } });
    }
    return out;
  };

  const activeCat = subcat || section;
  const visibleItems = useMemo(() => {
    if (!boot) return [];
    if (!activeCat) return boot.menu;
    const ids = new Set(subtreeIds(activeCat));
    return boot.menu.filter((m) => m.category_id && ids.has(m.category_id));
  }, [boot, activeCat]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openTable = async (t) => {
    setBusy(true);
    try {
      const { data } = await kioskApi("post", "/kiosk/pos/orders", { table_id: t.id });
      setOrder(data);
      setView("order");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open table");
    } finally {
      setBusy(false);
    }
  };

  const saveLines = async (lines) => {
    try {
      const { data } = await kioskApi("put", `/kiosk/pos/orders/${order.id}/lines`, { lines });
      setOrder(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const addItem = (mi) => {
    const lines = order.lines.map((l) => ({ menu_item_id: l.menu_item_id, qty: l.qty }));
    const ex = lines.find((l) => l.menu_item_id === mi.id);
    if (ex) ex.qty += 1; else lines.push({ menu_item_id: mi.id, qty: 1 });
    saveLines(lines);
  };

  const bump = (lineId, delta) => {
    const lines = order.lines
      .map((l) => ({ menu_item_id: l.menu_item_id, qty: l.id === lineId ? l.qty + delta : l.qty }))
      .filter((l) => l.qty > 0);
    saveLines(lines);
  };

  const voidOrder = async () => {
    try {
      await kioskApi("post", `/kiosk/pos/orders/${order.id}/void`);
      setOrder(null);
      setView("tables");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Void failed");
    }
  };

  const closeOut = async () => {
    setBusy(true);
    try {
      const { data } = await kioskApi("post", `/kiosk/pos/orders/${order.id}/close`, { tip: Number(tip) || 0 });
      setReceipt(data);
      setView("receipt");
      setClosing(false);
      setTip("");
      load();
      if (data.unlinked_items?.length) toast.warning(`Stock not depleted (no recipe): ${data.unlinked_items.join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close-out failed");
    } finally {
      setBusy(false);
    }
  };

  const printReceipt = async () => {
    try {
      const { data } = await kioskApi("post", `/kiosk/pos/orders/${receipt.id}/print`);
      toast.success(data.queued ? "Queued — bridge will print shortly" : "Sent to receipt printer");
    } catch (e) {
      if (e.response?.status === 409) {
        window.print();  // no printer configured — browser print fallback
      } else {
        toast.error(e.response?.data?.detail || "Print failed — trying browser print");
        window.print();
      }
    }
  };

  const catColour = (id) => catById[id]?.colour || "#A78BFA";

  if (!boot) return <div className="min-h-screen bg-[#2E2E35] text-white flex items-center justify-center">Loading POS…</div>;

  /* ---------- receipt ---------- */
  if (view === "receipt" && receipt) {
    return (
      <div className="min-h-screen bg-[#2E2E35] text-white flex flex-col items-center justify-center gap-6 px-6 pt-safe-lg pb-6" data-testid="kpos-receipt">
        <div className="bg-white text-black rounded-lg w-full max-w-sm p-6 shadow-xl">
          <p className="text-center font-heading font-black text-lg tracking-tight">MISE</p>
          <p className="text-center text-xs text-neutral-500 mb-3">Receipt {receipt.receipt_no}</p>
          <p className="text-center text-xs text-neutral-500 mb-4">{receipt.table_name} · {receipt.closed_by} · {fmtTime(receipt.closed_at)}</p>
          <div className="border-y border-dashed border-neutral-300 py-3 space-y-1.5 text-sm">
            {receipt.lines.map((l) => (
              <div key={l.id} className="flex justify-between"><span>{l.qty} × {l.name}</span><span className="num">{zar(l.line_total)}</span></div>
            ))}
          </div>
          <div className="pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="num" data-testid="kpos-receipt-subtotal">{zar(receipt.subtotal)}</span></div>
            <div className="flex justify-between"><span>Gratuity</span><span className="num" data-testid="kpos-receipt-tip">{zar(receipt.tip)}</span></div>
            <div className="flex justify-between font-black text-lg border-t border-neutral-300 pt-1"><span>Total</span><span className="num" data-testid="kpos-receipt-total">{zar(receipt.total)}</span></div>
          </div>
          {receipt.unlinked_items?.length > 0 && (
            <p className="text-[10px] text-amber-700 bg-amber-50 rounded px-2 py-1.5 mt-3">Not depleted (no recipe): {receipt.unlinked_items.join(", ")}</p>
          )}
        </div>
        <div className="flex gap-3 w-full max-w-sm">
          <button onClick={printReceipt} data-testid="kpos-print-btn"
            className="flex-1 bg-white/10 border border-white/20 rounded-xl py-4 font-semibold flex items-center justify-center gap-2 hover:bg-white/15 active:scale-95 transition-all">
            <Printer size={22} /> Print receipt
          </button>
          <button onClick={() => { setReceipt(null); setOrder(null); setView("tables"); }} data-testid="kpos-receipt-done"
            className="flex-1 bg-[#A78BFA] rounded-xl py-4 font-semibold hover:bg-[#8B5CF6] active:scale-95 transition-all">
            Done
          </button>
        </div>
        <ReceiptPrintBlock receipt={receipt} />
      </div>
    );
  }

  /* ---------- tables ---------- */
  if (view === "tables") {
    const openByTable = Object.fromEntries(boot.orders.map((o) => [o.table_id, o]));
    return (
      <div className="min-h-screen bg-[#2E2E35] text-white flex flex-col pt-safe" data-testid="kpos-tables">
        <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <BrandWordmark size={30} />
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/60" data-testid="kpos-staff-chip">
              {session.employee.first_name} {session.employee.last_name}
              {boot.shift && <span className="ml-2 text-[#A78BFA]">· Shift open</span>}
            </span>
            <button onClick={onExit} data-testid="kpos-exit" className="bg-white/10 rounded-lg px-4 py-2 text-sm hover:bg-white/15 transition-colors">
              <X size={16} className="inline mr-1" /> Exit POS
            </button>
          </div>
        </header>
        <div className="flex-1 px-6 py-6 overflow-y-auto">
          <h2 className="font-heading text-xl font-bold mb-4">Tables</h2>
          {boot.tables.length === 0 && <p className="text-white/50">No tables configured — ask a manager to add them in Menu Builder.</p>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {boot.tables.map((t) => {
              const o = openByTable[t.id];
              return (
                <button key={t.id} onClick={() => !busy && openTable(t)} data-testid={`kpos-table-${t.id}`}
                  className={`rounded-2xl p-6 min-h-[110px] text-left transition-all duration-150 active:scale-95 border ${
                    o ? "bg-[#A78BFA]/15 border-[#A78BFA]/50" : "bg-white/5 border-white/10 hover:bg-white/10"}`}>
                  <p className="font-heading font-bold text-xl">{t.name}</p>
                  {o ? (
                    <p className="text-xs mt-2 text-[#C4B5FD] num" data-testid={`kpos-table-open-${t.id}`}>
                      {o.lines.reduce((a, l) => a + l.qty, 0)} item(s) · {zar(o.subtotal)}<br />{o.opened_by}
                    </p>
                  ) : (
                    <p className="text-xs mt-2 text-white/40">Available</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- order screen ---------- */
  return (
    <div className="h-screen bg-[#2E2E35] text-white flex flex-col overflow-hidden" data-testid="kpos-order">
      <header className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => { setOrder(null); setView("tables"); }} data-testid="kpos-back-tables"
            className="bg-white/10 rounded-lg p-2 hover:bg-white/15 transition-colors"><CaretLeft size={20} /></button>
          <div>
            <p className="font-heading font-bold text-lg leading-tight">{order.table_name}</p>
            <p className="text-[11px] text-white/50">opened {fmtTime(order.opened_at)} · {order.opened_by}</p>
          </div>
        </div>
        <span className="text-sm text-white/60">{session.employee.first_name} {session.employee.last_name}</span>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* category rail */}
        <nav className="w-28 md:w-36 shrink-0 border-r border-white/10 overflow-y-auto py-3 space-y-1.5 px-2" data-testid="kpos-cat-rail">
          <button onClick={() => { setSection(null); setSubcat(null); }} data-testid="kpos-cat-all"
            className={`w-full rounded-xl px-2 py-3.5 text-xs font-semibold text-center transition-colors ${!section ? "bg-[#A78BFA] text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            All
          </button>
          {sections.map((c) => (
            <button key={c.id} onClick={() => { setSection(c.id); setSubcat(null); }} data-testid={`kpos-cat-${c.id}`}
              className={`w-full rounded-xl px-2 py-3.5 text-xs font-semibold text-center transition-colors border-l-4 ${section === c.id ? "text-white" : "text-white/70 bg-white/5 hover:bg-white/10"}`}
              style={section === c.id ? { backgroundColor: c.colour || "#A78BFA", borderColor: "transparent" } : { borderColor: c.colour || "#A78BFA" }}>
              {c.name}
            </button>
          ))}
        </nav>

        {/* items */}
        <div className="flex-1 overflow-y-auto p-4">
          {section && childrenOf(section).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="kpos-subcats">
              {childrenOf(section).map((c) => (
                <button key={c.id} onClick={() => setSubcat(subcat === c.id ? null : c.id)} data-testid={`kpos-subcat-${c.id}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${subcat === c.id ? "text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
                  style={subcat === c.id ? { backgroundColor: c.colour || "#A78BFA" } : { border: `1px solid ${c.colour || "#A78BFA"}60` }}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="kpos-items">
            {visibleItems.map((m) => (
              <button key={m.id} onClick={() => addItem(m)} data-testid={`kpos-item-${m.id}`}
                className="rounded-2xl overflow-hidden text-left border border-white/10 active:scale-95 transition-all duration-150 min-h-[104px] flex flex-col"
                style={{ backgroundColor: `${catColour(m.category_id)}26`, borderColor: `${catColour(m.category_id)}55` }}>
                {m.has_image && (
                  <img src={`${API_BASE}/api/kiosk/pos/menu/${m.id}/image`} alt=""
                    className="w-full h-20 object-cover" loading="lazy" />
                )}
                <div className="p-3 flex-1 flex flex-col justify-between">
                  <p className="font-semibold text-sm leading-tight">{m.name}</p>
                  <p className="num text-sm font-bold mt-1.5" style={{ color: catColour(m.category_id) }}>{zar(m.price)}</p>
                </div>
              </button>
            ))}
            {visibleItems.length === 0 && <p className="text-white/40 col-span-full py-10 text-center">No items here yet.</p>}
          </div>
        </div>

        {/* order panel */}
        <aside className="w-72 md:w-80 shrink-0 border-l border-white/10 flex flex-col bg-[#26262C]">
          <div className="px-4 py-3 border-b border-white/10">
            <p className="font-heading font-bold">Current order</p>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2" data-testid="kpos-order-lines">
            {order.lines.length === 0 && <p className="text-white/40 text-sm py-6 text-center">Tap items to add them.</p>}
            {order.lines.map((l) => (
              <div key={l.id} className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2.5" data-testid={`kpos-line-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{l.name}</p>
                  <p className="text-[11px] text-white/50 num">{zar(l.line_total)}</p>
                </div>
                <button onClick={() => bump(l.id, -1)} data-testid={`kpos-minus-${l.id}`} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20"><Minus size={14} /></button>
                <span className="num w-5 text-center text-sm">{l.qty}</span>
                <button onClick={() => bump(l.id, 1)} data-testid={`kpos-plus-${l.id}`} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20"><Plus size={14} /></button>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 p-4 space-y-3">
            {closing ? (
              <div data-testid="kpos-closeout">
                <label className="text-xs text-white/60">Gratuity / tip (R)</label>
                <input type="number" min="0" step="any" value={tip} onChange={(e) => setTip(e.target.value)} autoFocus data-testid="kpos-tip"
                  className="w-full mt-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-lg num outline-none focus:border-[#A78BFA]" />
                <div className="flex justify-between items-baseline mt-3">
                  <span className="text-white/60 text-sm">Total</span>
                  <span className="font-heading font-black text-2xl num" data-testid="kpos-total">{zar(order.subtotal + (Number(tip) || 0))}</span>
                </div>
                <p className="text-[10px] text-white/40 mt-1">Card machine handles payment — marking paid posts the sale + tip and depletes stock.</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={closeOut} disabled={busy} data-testid="kpos-confirm-close"
                    className="flex-1 bg-[#A78BFA] rounded-xl py-3.5 font-bold hover:bg-[#8B5CF6] active:scale-95 transition-all disabled:opacity-50">
                    <Receipt size={18} className="inline mr-1.5" />Mark paid
                  </button>
                  <button onClick={() => setClosing(false)} data-testid="kpos-cancel-close"
                    className="bg-white/10 rounded-xl px-4 hover:bg-white/15 transition-colors">Back</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-baseline">
                  <span className="text-white/60 text-sm">Subtotal</span>
                  <span className="font-heading font-black text-2xl num" data-testid="kpos-subtotal">{zar(order.subtotal)}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setClosing(true)} disabled={!order.lines.length} data-testid="kpos-close-btn"
                    className="flex-1 bg-[#A78BFA] rounded-xl py-4 font-bold text-base hover:bg-[#8B5CF6] active:scale-95 transition-all disabled:opacity-40">
                    Close out
                  </button>
                  <button onClick={voidOrder} data-testid="kpos-void-btn"
                    className="bg-white/10 rounded-xl px-4 hover:bg-[#B33939]/60 transition-colors" title="Void order">
                    <Trash size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export function ReceiptPrintBlock({ receipt }) {
  return (
    <div id="pos-receipt-print" className="hidden print:block" data-testid="receipt-print-block">
      <p className="r-title">MISE</p>
      <p className="r-meta">Receipt {receipt.receipt_no}<br />{receipt.table_name} · {receipt.closed_by} · {fmtTime(receipt.closed_at)}</p>
      <table>
        <tbody>
          {receipt.lines.map((l) => (
            <tr key={l.id}><td>{l.qty} × {l.name}</td><td style={{ textAlign: "right" }}>{zar(l.line_total)}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="r-rule" />
      <table>
        <tbody>
          <tr><td>Subtotal</td><td style={{ textAlign: "right" }}>{zar(receipt.subtotal)}</td></tr>
          <tr><td>Gratuity</td><td style={{ textAlign: "right" }}>{zar(receipt.tip)}</td></tr>
          <tr className="r-total"><td>Total</td><td style={{ textAlign: "right" }}>{zar(receipt.total)}</td></tr>
        </tbody>
      </table>
      <div className="r-rule" />
      <p className="r-meta">Thank you — powered by MISE</p>
    </div>
  );
}
